<template>
  <div>
    <div v-show="visible" class="app-turnstile-dialog">
      <div ref="turnstileContainer" class="app-turnstile-container d-flex elevation-6"></div>
    </div>
    <app-turnstile-badge :position="badge"></app-turnstile-badge>
  </div>
</template>

<script>
import { logger } from '@/helpers/log.service';
import { actions as _act } from '@/models/store.actions';
import AppTurnstileBadge from '@/components/Main/AppTurnstileBadge.vue';
import { getLocale } from '@/helpers';

export default {
  name: 'AppTurnstileRecaptcha',
  components: { AppTurnstileBadge },
  props: {
    siteKey: { type: String, default: '' },
    badge: { type: String, default: '' }
  },
  data() {
    return {
      visible: false,
      widgetId: '',
      retryCount: 0,
      maxRetryCount: 2,
      retryTimeout: 1000,
      hideTimeoutOnError: 0,
      hideTimeoutOnSuccess: 0
    };
  },
  computed: {
    turnstile() { return window.turnstile; },
    theme() { return this.$vuetify.theme['isDark'] ? 'dark' : 'light'; },
    locale() { return this.$store.getters[_act.SETT_GET_LOCALEGET]; }
  },
  mounted() {
    this.render();
  },
  methods: {
    render() {
      if (typeof this.turnstile === 'undefined') return;

      // https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/#configurations
      // https://developers.cloudflare.com/turnstile/reference/testing/
      this.widgetId = this.turnstile.render(this.$refs.turnstileContainer, {
        // sitekey: '1x00000000000000000000AA', // Always passes
        // sitekey: '2x00000000000000000000AB', // Always blocks
        // sitekey: '3x00000000000000000000FF', // Forces an interactive challenge
        sitekey: this.siteKey,
        appearance: 'interaction-only', // 'always' (default), 'execute', 'interaction-only'
        execution: 'execute', // 'render' (default), 'execute'
        theme: this.theme,
        language: this.locale,
        size: 'normal',
        retry: 'never',
        'refresh-expired': 'never',
        callback: (token) => {
          logger.debug(`[AppTurnstileRecaptcha:Success:Callback] token="${token}"`);
          setTimeout(() => {
            this.visible = false;
            this.$emit('verify', token);
            this.turnstile.reset(this.widgetId);
          }, this.hideTimeoutOnSuccess);
        },
        'error-callback': (err) => {
          logger.debug(`[AppTurnstileRecaptcha:Error:Callback]`, err);
          this.retryCount += 1;
          if (this.retryCount <= this.maxRetryCount) {
            logger.debug(`[AppTurnstileRecaptcha:Error:Callback] scheduling retry in ${this.retryTimeout / 1000}sec`);
            this.turnstile.reset(this.widgetId);
            setTimeout(() => {
              this.turnstile.execute(this.widgetId);
            }, this.retryTimeout);
          } else {
            setTimeout(() => {
              this.visible = false;
              this.$emit('error', new Error(getLocale('appRecaptcha.recaptchaVerificationError').toString()));
            }, this.hideTimeoutOnError);
          }
        },
        'unsupported-callback': (e) => {
          logger.debug(`[AppTurnstileRecaptcha:Unsupported:Callback]`, e);
          setTimeout(() => {
            this.turnstile.reset(this.widgetId);
            this.visible = false;
            this.$emit('error', new Error(getLocale('appRecaptcha.turnstileIsNotSupported').toString()));
          }, this.hideTimeoutOnError);
        }
      });
      logger.debug(`[AppTurnstileRecaptcha:render] widgetId="${this.widgetId}"`);
    },
    execute() {
      logger.debug(`[AppTurnstileRecaptcha:execute]`, this.widgetId);
      this.retryCount = 1;
      this.visible = true;
      this.turnstile.reset(this.widgetId);
      this.turnstile.execute(this.widgetId);
    }
  }
};
</script>

<style lang="scss">
.app-turnstile-dialog {
  align-items: center;
  background-color: transparent;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  min-width: 100vw;
  left: 0;
  position: fixed;
  top: 0;
  z-index: 10000
}
</style>
